import React, { useState } from 'react';
import { useFinancialAdvisor } from '../hooks/useApi';

const AIInsights = ({ dateRange }) => {
  const [expanded, setExpanded] = useState(false);
  
  const { data: insights, isLoading, error } = useFinancialAdvisor({
    startDate: dateRange?.startDate,
    endDate: dateRange?.endDate
  });

  const toggleExpanded = () => setExpanded(!expanded);

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-5 h-5 text-blue-600">💡</div>
          <h3 className="text-lg font-semibold text-gray-800">AI Insights</h3>
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="text-sm text-gray-600">
          Analyzing your financial data...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">
            Unable to load AI insights. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  if (!insights) {
    return null;
  }

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getScoreIcon = (score) => {
    if (score >= 70) return '📈';
    return '📉';
  };

  const getTrendColor = (trend) => {
    if (trend && trend.includes('improving')) return 'text-green-600 bg-green-50 border-green-200';
    if (trend && trend.includes('worsening')) return 'text-red-600 bg-red-50 border-red-200';
    return 'text-blue-600 bg-blue-50 border-blue-200';
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return 'text-red-600 bg-red-50 border-red-200';
    if (priority === 'medium') return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getSeverityColor = (severity) => {
    if (severity === 'high') return 'text-red-600 bg-red-50 border-red-200';
    if (severity === 'medium') return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
      {/* Header with summary */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="text-blue-600">💡</div>
          <h3 className="text-lg font-semibold text-gray-800">AI Insights</h3>
          {insights.overallScore && (
            <div className="flex items-center gap-2">
              <span className="text-sm">{getScoreIcon(insights.overallScore)}</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-md border ${getScoreColor(insights.overallScore)}`}>
                Score: {insights.overallScore}/100
              </span>
            </div>
          )}
        </div>
        <button
          onClick={toggleExpanded}
          className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
        >
          {expanded ? 'Less' : 'More'}
          <span className="text-xs">{expanded ? '▲' : '▼'}</span>
        </button>
      </div>

      {/* Always visible summary */}
      <p className="text-sm text-gray-600 mb-3">
        {insights.summary || 'AI analysis of your current spending patterns and trends.'}
      </p>

      {/* Key highlights - always visible */}
      {insights.keyHighlights && insights.keyHighlights.length > 0 && (
        <div className="mb-3">
          <div className="flex gap-2 flex-wrap">
            {insights.keyHighlights.slice(0, 3).map((highlight, index) => (
              <span
                key={index}
                className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-md"
              >
                {highlight}
              </span>
            ))}
            {insights.keyHighlights.length > 3 && !expanded && (
              <button
                onClick={toggleExpanded}
                className="px-2 py-1 text-xs bg-gray-50 text-gray-700 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
              >
                +{insights.keyHighlights.length - 3} more
              </button>
            )}
          </div>
        </div>
      )}

      {/* Show trend analysis as chips when available */}
      {insights.trend_analysis && (
        <div className="mb-3">
          <div className="flex gap-2 flex-wrap">
            {insights.trend_analysis.spending_trend && (
              <span className={`px-2 py-1 text-xs font-medium rounded-md border ${getTrendColor(insights.trend_analysis.spending_trend)}`}>
                Trend: {insights.trend_analysis.spending_trend}
              </span>
            )}
            {insights.trend_analysis.apples_to_apples_change && (
              <span className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-md">
                {insights.trend_analysis.apples_to_apples_change}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Expandable detailed content */}
      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {/* Detailed Analysis */}
          {insights.detailedAnalysis && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2">Detailed Analysis</h4>
              <p className="text-sm text-gray-600">{insights.detailedAnalysis}</p>
            </div>
          )}

          {/* Trend Analysis */}
          {insights.trend_analysis && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2">Trend Analysis</h4>
              <div className="space-y-1">
                <p className="text-sm text-gray-600">
                  Spending Trend: <span className="font-medium">{insights.trend_analysis.spending_trend}</span>
                </p>
                {insights.trend_analysis.daily_rate_comparison && (
                  <p className="text-sm text-gray-600">
                    Daily Rate: {insights.trend_analysis.daily_rate_comparison}
                  </p>
                )}
                {insights.trend_analysis.projected_vs_last_month_full && (
                  <p className="text-sm text-gray-600">
                    Projection: {insights.trend_analysis.projected_vs_last_month_full}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Projections */}
          {insights.projections && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2">Monthly Projections</h4>
              <div className="space-y-1">
                {insights.projections.projected_month_end_total && (
                  <p className="text-sm text-gray-600">
                    Projected Month-End Total: {insights.projections.projected_month_end_total}
                  </p>
                )}
                {insights.projections.projected_budget_status && (
                  <p className="text-sm text-gray-600">
                    Budget Status: <span className="font-medium">{insights.projections.projected_budget_status}</span>
                  </p>
                )}
                {insights.projections.recommended_daily_limit && (
                  <p className="text-sm text-gray-600">
                    Recommended Daily Limit: {insights.projections.recommended_daily_limit}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Budget Analysis */}
          {insights.budget_analysis && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2">Budget Analysis</h4>
              <div className="space-y-1">
                {insights.budget_analysis.current_month && (
                  <p className="text-sm text-gray-600">
                    Current Month: {insights.budget_analysis.current_month.actual_spending} of {insights.budget_analysis.current_month.discretionary_budget}
                    {insights.budget_analysis.current_month.budget_utilization && ` (${insights.budget_analysis.current_month.budget_utilization})`}
                  </p>
                )}
                {insights.budget_analysis.performance_comparison && (
                  <p className="text-sm text-gray-600">
                    Performance: <span className="font-medium">{insights.budget_analysis.performance_comparison}</span> vs same period last month
                  </p>
                )}
              </div>
            </div>
          )}

          {/* All highlights when expanded */}
          {insights.keyHighlights && insights.keyHighlights.length > 3 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2">All Highlights</h4>
              <div className="flex gap-2 flex-wrap">
                {insights.keyHighlights.map((highlight, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-md"
                  >
                    {highlight}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {insights.recommendations && insights.recommendations.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-blue-600">💡</span>
                Recommendations
              </h4>
              <div className="space-y-3">
                {insights.recommendations.map((rec, index) => (
                  <div key={index} className="bg-gray-50 p-3 rounded-md">
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-sm font-medium text-gray-800">
                        {rec.action || rec.title || rec}
                      </p>
                      {rec.priority && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-md border ${getPriorityColor(rec.priority)}`}>
                          {rec.priority}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {rec.category && (
                        <p className="text-xs text-gray-600">Category: {rec.category}</p>
                      )}
                      {rec.potential_savings && (
                        <p className="text-xs text-gray-600">Potential Savings: {rec.potential_savings}</p>
                      )}
                      {rec.rationale && (
                        <p className="text-xs text-gray-600">{rec.rationale}</p>
                      )}
                      {rec.description && (
                        <p className="text-xs text-gray-600">{rec.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Alerts & Areas of Concern */}
          {insights.alerts && insights.alerts.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-yellow-600">⚠️</span>
                Alerts & Areas of Concern
              </h4>
              <div className="space-y-3">
                {insights.alerts.map((alert, index) => (
                  <div key={index} className="bg-yellow-50 p-3 rounded-md border border-yellow-200">
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-sm font-medium text-yellow-800">
                        {alert.message || alert.title || alert}
                      </p>
                      {alert.severity && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-md border ${getSeverityColor(alert.severity)}`}>
                          {alert.severity}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      {alert.category && (
                        <p className="text-xs text-yellow-700">Category: {alert.category}</p>
                      )}
                      {alert.current_month_amount && (
                        <p className="text-xs text-yellow-700">Current: {alert.current_month_amount}</p>
                      )}
                      {alert.last_month_amount && (
                        <p className="text-xs text-yellow-700">Last Month: {alert.last_month_amount}</p>
                      )}
                      {alert.budget_allocated && (
                        <p className="text-xs text-yellow-700">Budget: {alert.budget_allocated}</p>
                      )}
                      {alert.description && (
                        <p className="text-xs text-yellow-700">{alert.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Legacy warnings support */}
          {insights.warnings && insights.warnings.length > 0 && !insights.alerts && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-yellow-600">⚠️</span>
                Areas of Concern
              </h4>
              <div className="space-y-2">
                {insights.warnings.map((warning, index) => (
                  <div key={index} className="bg-yellow-50 p-3 rounded-md border border-yellow-200">
                    <p className="text-sm font-medium text-yellow-800">
                      {warning.title || warning}
                    </p>
                    {warning.description && (
                      <p className="text-xs text-yellow-700 mt-1">{warning.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Encouragement */}
          {insights.encouragement && insights.encouragement.length > 0 && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-green-600">📈</span>
                Positive Progress
              </h4>
              <div className="space-y-2">
                {insights.encouragement.map((message, index) => (
                  <div key={index} className="bg-green-50 p-3 rounded-md border border-green-200">
                    <p className="text-sm text-green-800">{message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comparison Data */}
          {insights.comparison && (
            <div className="mb-2">
              <h4 className="font-medium text-gray-800 mb-2">Month-to-Month Comparison</h4>
              <p className="text-sm text-gray-600 mb-1">
                {insights.comparison.summary || 'Comparing current spending patterns with the same period last month.'}
              </p>
              {insights.comparison.percentageChange && (
                <p className="text-xs text-gray-500">
                  Change: {insights.comparison.percentageChange > 0 ? '+' : ''}{insights.comparison.percentageChange}%
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIInsights;
